import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiEndpoints } from '../../constants/api.endpoints';
import { AuthService } from './auth.service';

export interface SeoMetaTag {
  id: string;
  pageKey: string;
  pageName: string;
  metaTitle: string;
  metaDescription: string;
  metaKeywords?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
  canonicalUrl?: string;
  noIndex: boolean;
  noFollow: boolean;
  isActive: boolean;
  priority: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateSeoMetaTagRequest {
  pageKey: string;
  pageName: string;
  metaTitle: string;
  metaDescription: string;
  metaKeywords?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
  canonicalUrl?: string;
  noIndex?: boolean;
  noFollow?: boolean;
  isActive?: boolean;
  priority?: number;
}

export interface UpdateSeoMetaTagRequest {
  pageName?: string;
  metaTitle?: string;
  metaDescription?: string;
  metaKeywords?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
  canonicalUrl?: string;
  noIndex?: boolean;
  noFollow?: boolean;
  isActive?: boolean;
  priority?: number;
}

export interface SeoMetaTagsApiResponse {
  successful: boolean;
  message: string;
  response: SeoMetaTag[];
}

export interface SeoMetaTagApiResponse {
  successful: boolean;
  message: string;
  response: SeoMetaTag;
}

@Injectable({
  providedIn: 'root'
})
export class SeoMetaTagService {
  private readonly adminBaseUrl = `${ApiEndpoints.BASE_URL}/api/settings/super-admin/seo/meta-tags`;
  private readonly publicBaseUrl = `${ApiEndpoints.BASE_URL}/api/settings/seo/meta-tags`;

  constructor(
    private readonly http: HttpClient,
    private readonly authService: AuthService
  ) {}

  getAllSeoMetaTags(): Observable<SeoMetaTagsApiResponse> {
    return this.http.get<any>(this.adminBaseUrl, {
      headers: this.authService.getHeaders()
    }).pipe(
      map((response) => ({
        successful: !!response?.successful,
        message: response?.message || '',
        response: response?.response || []
      }))
    );
  }

  getSeoMetaTagById(id: string): Observable<SeoMetaTagApiResponse> {
    return this.http.get<any>(`${this.adminBaseUrl}/${id}`, {
      headers: this.authService.getHeaders()
    }).pipe(
      map((response) => ({
        successful: !!response?.successful,
        message: response?.message || '',
        response: response?.response
      }))
    );
  }

  getSeoMetaTagByPageKeyAdmin(pageKey: string): Observable<SeoMetaTagApiResponse> {
    return this.http.get<any>(`${this.adminBaseUrl}/page/${pageKey}`, {
      headers: this.authService.getHeaders()
    }).pipe(
      map((response) => ({
        successful: !!response?.successful,
        message: response?.message || '',
        response: response?.response
      }))
    );
  }

  createSeoMetaTag(request: CreateSeoMetaTagRequest): Observable<SeoMetaTagApiResponse> {
    return this.http.post<any>(this.adminBaseUrl, request, {
      headers: this.authService.getHeaders()
    }).pipe(
      map((response) => ({
        successful: !!response?.successful,
        message: response?.message || '',
        response: response?.response
      }))
    );
  }

  updateSeoMetaTag(id: string, request: UpdateSeoMetaTagRequest): Observable<SeoMetaTagApiResponse> {
    return this.http.put<any>(`${this.adminBaseUrl}/${id}`, request, {
      headers: this.authService.getHeaders()
    }).pipe(
      map((response) => ({
        successful: !!response?.successful,
        message: response?.message || '',
        response: response?.response
      }))
    );
  }

  updateSeoMetaTagByPageKey(pageKey: string, request: UpdateSeoMetaTagRequest): Observable<SeoMetaTagApiResponse> {
    return this.http.put<any>(`${this.adminBaseUrl}/page/${pageKey}`, request, {
      headers: this.authService.getHeaders()
    }).pipe(
      map((response) => ({
        successful: !!response?.successful,
        message: response?.message || '',
        response: response?.response
      }))
    );
  }

  deleteSeoMetaTag(id: string): Observable<unknown> {
    return this.http.delete(`${this.adminBaseUrl}/${id}`, {
      headers: this.authService.getHeaders()
    });
  }

  toggleActiveStatus(id: string): Observable<SeoMetaTagApiResponse> {
    return this.http.patch<any>(`${this.adminBaseUrl}/${id}/toggle`, {}, {
      headers: this.authService.getHeaders()
    }).pipe(
      map((response) => ({
        successful: !!response?.successful,
        message: response?.message || '',
        response: response?.response
      }))
    );
  }

  getActiveSeoMetaTags(): Observable<SeoMetaTagsApiResponse> {
    return this.http.get<any>(`${this.publicBaseUrl}/active`).pipe(
      map((response) => ({
        successful: !!response?.successful,
        message: response?.message || '',
        response: response?.response || []
      }))
    );
  }

  getSeoMetaTagByPageKey(pageKey: string): Observable<SeoMetaTagApiResponse> {
    return this.http.get<any>(`${this.publicBaseUrl}/page/${pageKey}`).pipe(
      map((response) => ({
        successful: !!response?.successful,
        message: response?.message || '',
        response: response?.response
      }))
    );
  }
}
